import { APIEndpoints, DYNAMIC_ENDPOINTS } from '@/@crosstab/types/constants';
import type {
    CrosstabConfig,
    CrosstabListParams,
    CrosstabListResponse,
} from '@/@crosstab/types/entities';
import type { DisplaySettings, PivotTableProps } from '@/@crosstab/engine/types';
import { EVENTS } from '@/lib/features/tracking';
import { tracking } from '@/lib/features/tracking';
import { portalApi } from '@/lib/http/axios';
import type { AxiosInstance } from 'axios';

const FEATURE_NAME = 'crosstab';

/**
 * Question shape returned by `GET /studio/v1/crosstab/:layoutKey/data`.
 * Snake-cased option keys match the engine contract in `@crosstab/engine`.
 */
export interface CrosstabQuestion {
    id: string;
    qid: string;
    question: string;
    sortOrder: number;
    questionType: string;
    min?: number | null;
    max?: number | null;
    matrixOptions: Record<string, string[]>;
    options: {
        option_id: string[];
        sort_order: number;
        option: string;
        is_deletable?: boolean;
        question_group?: string;
    }[];
}

export interface CrosstabDataResponse {
    projectId: string;
    surveyId?: string;
    surveyIds: string[];
    isInternalUser: boolean;
    questions: CrosstabQuestion[];
    /** `[headers, ...rows]` — first row is `[panelist_id, ...uppercased question IDs]`. */
    /**
     * `[headers, ...rows]`. Cell values match atlas `formatResponses`: strings
     * are uppercased, numbers / dates / `null` pass through unchanged.
     */
    responses: unknown[][];
    totalRespondents: number;
}

/**
 * Layout shape returned by `GET /studio/v1/crosstab/:layoutKey`. `statTesting` is
 * stored serialised on the wire and parsed into `number[]` by the consumer.
 */
export interface RawCrosstabLayoutData {
    name: string;
    projectId: number;
    projectName?: string;
    surveyId: number | null;
    isInternalUser: boolean;
    layout: {
        version: string;
        settings: Omit<DisplaySettings, 'statTesting'> & {
            statTesting?: string;
            filter?: unknown;
            matrixSummaryGroups?: string;
        };
        config: PivotTableProps[][];
    };
}

/** Outbound payload for `PATCH /studio/v1/crosstab/:layoutKey`. */
export interface SaveCrosstabLayoutPayload {
    name: string;
    layout: {
        version: string;
        settings: Omit<DisplaySettings, 'statTesting'> & {
            statTesting: string;
            filter?: unknown;
            matrixSummaryGroups?: string;
        };
        config: PivotTableProps[][];
    };
}

interface ExportSettingsPayload extends DisplaySettings {
    filter?: unknown;
    matrixSummaryGroups?: string;
}

interface ExportLayoutResponse {
    data: string;
    fileName: string;
}

class CrosstabService {
    private readonly axiosInstance: AxiosInstance;

    constructor() {
        this.axiosInstance = portalApi;
    }

    async loadData(layoutKey: string): Promise<CrosstabDataResponse> {
        try {
            const { data: response } = await this.axiosInstance.get(
                `${APIEndpoints.CROSSTAB}/${layoutKey}/data`,
            );
            return (response.data || response) as CrosstabDataResponse;
        } catch (error) {
            tracking.errorWithSource(error as Error, `${FEATURE_NAME}.loadData`);
            throw error;
        }
    }

    async getOrgCrosstabs(params: CrosstabListParams): Promise<CrosstabListResponse> {
        try {
            const { data: response } = await this.axiosInstance.get(APIEndpoints.CROSSTAB_LIST, {
                params: {
                    projectId: params.projectId || '',
                    surveyId: params.surveyId || '',
                    page: params.page || 1,
                    search: params.search || '',
                    sortBy: params.sortBy || 'updatedAt',
                    sortOrder: params.sortOrder || 'desc',
                    ...(params.subsidiaryOrgIds?.length
                        ? { subsidiaryOrgIds: params.subsidiaryOrgIds.join(',') }
                        : {}),
                },
            });
            return (response.data || response) as CrosstabListResponse;
        } catch (error) {
            tracking.errorWithSource(error as Error, 'CrosstabService.getOrgCrosstabs');
            throw error;
        }
    }

    async getCrosstab(crosstabId: string): Promise<RawCrosstabLayoutData> {
        try {
            const { data: response } = await this.axiosInstance.get(
                `${APIEndpoints.CROSSTAB}/${crosstabId}`,
            );
            return (response.data || response) as RawCrosstabLayoutData;
        } catch (error) {
            tracking.errorWithSource(error as Error, 'CrosstabService.getCrosstab');
            throw error;
        }
    }

    async createCrosstab(
        layoutName: string,
        projectId: number,
        surveyId: number | null,
        targetOrgId?: number,
    ) {
        try {
            const { data: response } = await this.axiosInstance.post(
                DYNAMIC_ENDPOINTS.CREATE_CROSSTAB(projectId, surveyId),
                {
                    layoutName,
                    ...(targetOrgId != null ? { targetOrgId } : {}),
                },
            );
            const unwrapped = response.data || response;
            return unwrapped.key;
        } catch (error) {
            tracking.errorWithSource(error as Error, 'CrosstabService.createCrosstab');
            throw error;
        }
    }

    async updateCrosstab(crosstab: CrosstabConfig) {
        try {
            const { data: response } = await this.axiosInstance.patch(
                `${APIEndpoints.CROSSTAB}/${crosstab.key}`,
                { layout: crosstab.layout, name: crosstab.name },
            );
            return response.data || response;
        } catch (error) {
            tracking.errorWithSource(error as Error, 'CrosstabService.updateCrosstab');
            throw error;
        }
    }

    async saveCrosstabLayout(layoutKey: string, payload: SaveCrosstabLayoutPayload) {
        try {
            const { data: response } = await this.axiosInstance.patch(
                `${APIEndpoints.CROSSTAB}/${layoutKey}`,
                payload,
            );
            return response.data || response;
        } catch (error) {
            tracking.errorWithSource(error as Error, 'CrosstabService.saveCrosstabLayout');
            throw error;
        }
    }

    /**
     * Server-side Excel export. Returns the XLSX file as a Blob ready for download.
     * Backend responds with `{ data: base64String, fileName }`.
     */
    async exportLayout(
        layoutKey: string,
        data: unknown,
        settings: ExportSettingsPayload,
    ): Promise<{ blob: Blob; fileName: string }> {
        try {
            const { data: response } = await this.axiosInstance.post(
                `${APIEndpoints.CROSSTAB}/export/${layoutKey}`,
                { data, settings },
            );
            const payload = (response.data || response) as ExportLayoutResponse;
            const base64 = payload.data ?? '';
            const fileName = payload.fileName ?? `crosstab-${layoutKey}.xlsx`;
            const binary = atob(base64);
            const bytes = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i++) {
                bytes[i] = binary.charCodeAt(i);
            }
            const blob = new Blob([bytes], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            return { blob, fileName };
        } catch (error) {
            tracking.errorWithSource(error as Error, 'CrosstabService.exportLayout');
            throw error;
        }
    }

    async duplicateCrosstab(
        layoutName: string,
        crosstabId: string,
        projectId: number,
        surveyId?: number,
    ) {
        try {
            const { data: response } = await this.axiosInstance.post(
                DYNAMIC_ENDPOINTS.DUPLICATE_CROSSTAB(crosstabId, projectId, surveyId),
                { layoutName },
            );
            const unwrapped = response.data || response;
            return unwrapped.key;
        } catch (error) {
            tracking.errorWithSource(error as Error, 'CrosstabService.duplicateCrosstab');
            throw error;
        }
    }

    async deleteCrosstab(crosstabId: string) {
        try {
            const { data: response } = await this.axiosInstance.delete(
                `${APIEndpoints.CROSSTAB}/${crosstabId}`,
            );
            return response.data || response;
        } catch (error) {
            tracking.errorWithSource(error as Error, 'CrosstabService.deleteCrosstab');
            throw error;
        }
    }

    async renameCrosstab(crosstabId: string, newName: string) {
        try {
            const { data: response } = await this.axiosInstance.patch(
                `${APIEndpoints.CROSSTAB}/${crosstabId}/rename`,
                { newName },
            );
            return response.data || response;
        } catch (error) {
            tracking.errorWithSource(error as Error, 'CrosstabService.renameCrosstab');
            throw error;
        }
    }

    async uploadOpenEnds(layoutKey: string, formData: FormData) {
        try {
            const { data: response } = await this.axiosInstance.post(
                `${APIEndpoints.CROSSTAB}/open-end/upload/${layoutKey}`,
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } },
            );
            return response.data || response;
        } catch (error) {
            tracking.errorWithSource(error as Error, 'CrosstabService.uploadOpenEnds');
            throw error;
        }
    }

    /** Toggle publish; pass body to target a specific account/org. */
    async publishLayout(crosstabId: string, body?: { sfAccountId?: string; orgId?: string }) {
        try {
            const { data: response } = await this.axiosInstance.patch(
                `${APIEndpoints.PUBLISH_CROSSTAB}/${crosstabId}`,
                body ?? {},
            );
            return response.data || response;
        } catch (error) {
            tracking.errorWithSource(error as Error, 'CrosstabService.publishLayout');
            throw error;
        }
    }

    /** Backwards-compat alias for the list "toggle publish" action. */
    async togglePublishCrosstab(crosstabId: string) {
        return this.publishLayout(crosstabId);
    }
}

export const crosstabService = new CrosstabService();
export default CrosstabService;

// Useful tracking event surface for callers.
export { EVENTS };
